'use client'

import Image from 'next/image'
import { useRouter } from '@/i18n/navigation'
import { useLocale } from 'next-intl'
import { trackAffiliateClick } from '@/utils/analytics'
import { formatCommission } from '@/data/indexData'

export interface BrokerRec {
  id: string
  name: string
  commissionPct: number | null
  logo: string
  logoBg?: string
}

// Marka adından 2-harf kısaltma türetir.
// "BtcTurk" → "BT", "Binance TR" → "BT", "Binance" → "BN", "OKX" → "OK"
function brokerInitials(name: string): string {
  const cleaned = name.trim()
  // İki kelime varsa her birinin ilk harfini al
  const words = cleaned.split(/\s+/)
  if (words.length >= 2) {
    return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase()
  }
  // Tek kelime ise ilk 2 harfini al
  return cleaned.slice(0, 2).toUpperCase()
}

interface Props {
  brokers: BrokerRec[]
  symbol?: string
  source: string
  limit?: number
  /** Override default headline (TR ya da EN — locale-aware caller'a bırak) */
  customHeadline?: string
  customSubline?: string
  customCta?: string
}

export default function BrokerRecommendations({
  brokers, symbol, source, limit,
  customHeadline, customSubline, customCta,
}: Props) {
  const router = useRouter()
  const lang = useLocale()
  // Defansif: brokers prop undefined gelirse (data import sorunu, HMR cache vs.) crash etme
  if (!brokers || brokers.length === 0) return null
  // limit verilmediyse hepsini göster (kullanıcı ne kadar broker geçtiyse o kadar)
  const items = limit ? brokers.slice(0, limit) : brokers

  const headline = customHeadline ?? (lang === 'en'
    ? (symbol ? `Where to buy ${symbol}?` : 'Where to invest?')
    : (symbol ? `${symbol} hissesini nereden alırım?` : 'Yatırım için aracı kurumlar'))

  const subline = customSubline ?? (lang === 'en'
    ? 'Trusted brokers offering this stock — compare commissions and platforms'
    : 'Bu hisseyi sunan güvenilir aracı kurumlar — komisyonları ve platformları karşılaştırın')

  const ctaLabel = customCta ?? (lang === 'en' ? 'Compare all brokers →' : 'Tümünü karşılaştır →')

  return (
    <section
      className="rounded-xl overflow-hidden mb-6 p-5 sm:p-6"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--accent-color) 8%, var(--bg-card))',
        border: '2px solid color-mix(in srgb, var(--accent-color) 40%, transparent)',
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-extrabold m-0 mb-1 flex items-center gap-2" style={{ color: 'var(--text-main)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            {headline}
          </h2>
          <p className="text-[12px] sm:text-[13px] m-0" style={{ color: 'var(--text-muted)' }}>
            {subline}
          </p>
        </div>
        <button
          type="button"
          onClick={() => router.push('/brokers' as never)}
          className="px-4 py-2 rounded-lg text-[12px] sm:text-[13px] font-semibold transition-all hover:brightness-95 shrink-0"
          style={{ backgroundColor: 'var(--accent-color)', color: '#fff', border: 'none', cursor: 'pointer' }}
        >
          {ctaLabel}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
        {items.map(b => (
          <button
            key={b.id}
            type="button"
            onClick={() => {
              trackAffiliateClick(b.id, b.name, source)
              router.push('/brokers' as never)
            }}
            className="flex flex-col items-center gap-2.5 p-4 rounded-xl border-none cursor-pointer transition-all duration-200 hover:scale-[1.04] hover:shadow-md"
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
          >
            {b.logo ? (
              // SVG/PNG logo — brand bg üstünde padding'le ortalanmış, beyaz logo iyi okunur
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
                style={{ backgroundColor: b.logoBg || 'transparent' }}
              >
                <Image
                  src={b.logo}
                  alt={b.name}
                  width={36}
                  height={36}
                  className="w-9 h-9 object-contain"
                  unoptimized
                />
              </div>
            ) : (
              // Logo yok → 2-harf brand avatar (BT, PA, KR, vs.). brand bg + beyaz harf
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-[18px] font-extrabold shrink-0 tracking-tight"
                style={{ backgroundColor: b.logoBg || 'var(--accent-color)', color: '#fff' }}
              >
                {brokerInitials(b.name)}
              </div>
            )}
            <span className="text-[13px] font-semibold text-center leading-tight" style={{ color: 'var(--text-main)' }}>{b.name}</span>
            <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--secondary-color)', color: '#fff' }}>{formatCommission(b.commissionPct, lang)}</span>
          </button>
        ))}
      </div>
    </section>
  )
}
